// src/routes.js
// ─────────────────────────────────────────────────────────────────────────────
// Lehigh University — Mindful Steps Walking Routes
//
// Source: Campus Walkability Index audit data (TOT6%, AE1 Trees, AE2 Greenspace,
//         AC4 Gradient: 5=flat → 1=steep)
// Walking speed: 72 m/min (slow mindful pace)
//
// Route matrix:
//   Flat  × 10 min → ridge_flat_10    (CSV-traced,  688 m, avg AC4=4.8)
//   Hilly × 10 min → hills_loop_10    (CSV-traced,  682 m, avg AC4=2.1)
//   Flat  × 20 min → greenway_flat_20 (CSV-traced, 1397 m, avg AC4=4.1)
//   Hilly × 20 min → south_hilly_20   (ORS with south-campus waypoints)
// ─────────────────────────────────────────────────────────────────────────────

export const LEHIGH_ROUTES = [

    // ═══════════════════════════════════════════════════════════════════════════
    // ROUTE A — "Ridge Trail Loop"  |  FLAT  |  10 min
    // Distance: 688 m  |  ~9.6 min at 72 m/min
    // Walkability: 72.9%  |  Green score: 87/100  |  Avg gradient AC4: 4.8 (flat)
    // Segments: 010→011→008→009→305r→304r→124r→127r→128r→125r→115r→018→019→021r
    // Emotions best matched: Calm, Anxious, Low (short, sheltered, very green)
    // ═══════════════════════════════════════════════════════════════════════════
    {
      id: 'ridge_flat_10',
      name: 'Ridge Trail Loop',
      terrain: 'flat',
      durationMin: 10,
      distanceM: 688,
      walkScorePct: 72.9,
      greenScore: 87,
      avgGradient: 4.8,           // AC4 average: 5=flat, 1=steep
      emotions: ['Calm', 'Anxious', 'Low', 'Happy'],
      meditationSpot: [40.60711, -75.37567], // seg 012 — AE1=5 AE2=5, 81.5%
      coordinates: [
        [40.60724, -75.37828],  // START
        [40.60707, -75.37781],  // seg 010  AC4=5  75.4%
        [40.60705, -75.37731],  // seg 011  AC4=5  76.9%
        [40.60703, -75.37686],  // seg 008  AC4=5  76.9%
        [40.60711, -75.37567],  // seg 009  AC4=5  78.5%
        [40.60689, -75.37563],  // seg 305r AC4=5  73.8%
        [40.60684, -75.37606],  // seg 305r→304r
        [40.60681, -75.37637],  // seg 304r AC4=3  66.2%
        [40.60647, -75.37736],  // seg 124r AC4=3  66.9%
        [40.60654, -75.37748],  // seg 127r AC4=5  76.9%
        [40.60652, -75.37847],  // seg 128r AC4=5  75.4%
        [40.60646, -75.37902],  // seg 125r AC4=5  76.9%
        [40.60708, -75.37907],  // seg 115r AC4=1  51.5%
        [40.60716, -75.37890],  // seg 018  AC4=5  73.8%
        [40.60724, -75.37845],  // seg 019  AC4=5  75.4%
        [40.60724, -75.37828],  // LOOP CLOSE via seg 021
      ],
    },
  
    // ═══════════════════════════════════════════════════════════════════════════
    // ROUTE B — "North Ridge Hills Loop"  |  HILLY  |  10 min
    // Distance: 682 m  |  ~9.5 min at 72 m/min
    // Walkability: 65.4%  |  Green score: 74/100  |  Avg gradient AC4: 2.1 (hilly)
    // Steep segments: 154r (AC4=1), 186r (AC4=1), 095 (AC4=1) = 3 of 12 segments
    // Segments: 154r→186r→027r→037r→038r→095→016→020→017→018→019→021r
    // Emotions best matched: Energized, Happy (active, varied elevation)
    // ═══════════════════════════════════════════════════════════════════════════
    {
      id: 'hills_loop_10',
      name: 'North Ridge Hills Loop',
      terrain: 'hilly',
      durationMin: 10,
      distanceM: 682,
      walkScorePct: 65.4,
      greenScore: 74,
      avgGradient: 2.1,           // lower = hillier (AC4 avg across segments)
      emotions: ['Energized', 'Happy', 'Calm'],
      // Meditation spot: seg 016 start — AE1=5 AE2=5, 66.9%, open area after descent
      meditationSpot: [40.60708, -75.38104],
      coordinates: [
        [40.60724, -75.37828],  // START
        [40.60772, -75.37830],  // seg 154r AC4=1 STEEP  72.3%  53m
        [40.60822, -75.37833],  // seg 186r AC4=1 STEEP  66.9%  56m
        [40.60811, -75.37961],  // seg 027r AC4=5 flat   63.8% 109m
        [40.60800, -75.38084],  // seg 037r AC4=5 flat   63.1% 105m
        [40.60797, -75.38110],  // seg 038r AC4=5 flat   63.1%  22m
        [40.60708, -75.38104],  // seg 095  AC4=1 STEEP  58.5%  99m ← MEDITATION SPOT
        [40.60714, -75.38006],  // seg 016  AC4=5 flat   66.9%  83m
        [40.60716, -75.37945],  // seg 020  AC4=5 flat   70.0%  52m
        [40.60708, -75.37907],  // seg 017  AC4=5 flat   68.5%  33m
        [40.60716, -75.37890],  // seg 018  AC4=5 flat   73.8%  17m
        [40.60724, -75.37845],  // seg 019  AC4=5 flat   75.4%  39m
        [40.60724, -75.37828],  // LOOP CLOSE via seg 021        14m
      ],
    },
  
    // ═══════════════════════════════════════════════════════════════════════════
    // ROUTE C — "Greenway Extended Loop"  |  FLAT/MIXED  |  20 min
    // Distance: 1397 m  |  ~19.4 min at 72 m/min
    // Walkability: 71.7%  |  Green score: 82/100  |  Avg gradient AC4: 4.1
    // Segments: Route A core + 012→097r→096r + north arc→017r→020r→016r→095r
    //           →038→037→027→186→154 (loop)
    // Emotions best matched: Calm, Happy (scenic, long, mostly flat)
    // ═══════════════════════════════════════════════════════════════════════════
    {
      id: 'greenway_flat_20',
      name: 'Greenway Extended Loop',
      terrain: 'flat',
      durationMin: 20,
      distanceM: 1397,
      walkScorePct: 71.7,
      greenScore: 82,
      avgGradient: 4.1,
      emotions: ['Calm', 'Happy', 'Low', 'Anxious'],
      meditationSpot: [40.60652, -75.37847], // seg 128r — AE1=1 AE2=5, 75.4%
      coordinates: [
        [40.60724, -75.37828],  // START
        [40.60707, -75.37781],  // seg 010  AC4=5
        [40.60705, -75.37731],  // seg 011  AC4=5
        [40.60703, -75.37686],  // seg 008  AC4=5
        [40.60711, -75.37567],  // seg 009  AC4=5
        [40.60716, -75.37460],  // seg 012  AC4=5  81.5% ← highest score
        [40.60692, -75.37483],  // seg 097r AC4=1
        [40.60689, -75.37563],  // seg 096r AC4=5  80.0%
        [40.60684, -75.37606],  // seg 305r AC4=5
        [40.60681, -75.37637],  // seg 304r AC4=3
        [40.60647, -75.37736],  // seg 124r AC4=3
        [40.60654, -75.37748],  // seg 127r AC4=5
        [40.60652, -75.37847],  // seg 128r AC4=5  ← MEDITATION SPOT
        [40.60646, -75.37902],  // seg 125r AC4=5
        [40.60708, -75.37907],  // seg 115r AC4=1
        [40.60716, -75.37945],  // seg 017r AC4=5
        [40.60714, -75.38006],  // seg 020r AC4=5
        [40.60708, -75.38104],  // seg 016r AC4=5
        [40.60797, -75.38110],  // seg 095r AC4=1
        [40.60800, -75.38084],  // seg 038  AC4=5
        [40.60811, -75.37961],  // seg 037  AC4=5
        [40.60822, -75.37833],  // seg 027  AC4=5
        [40.60772, -75.37830],  // seg 186  AC4=1
        [40.60724, -75.37828],  // LOOP CLOSE via seg 154  AC4=1
      ],
    },
  
    // ═══════════════════════════════════════════════════════════════════════════
    // ROUTE D — "South Campus Hilly Loop"  |  HILLY  |  20 min
    // Uses ORS API with fixed Lehigh south-campus waypoints.
    // Waypoints routed through lower campus where terrain drops significantly
    // from the main ridge. ORS foot-walking profile keeps it pedestrian-only.
    // orsWaypoints format: [lng, lat] as required by ORS API.
    // ═══════════════════════════════════════════════════════════════════════════
    {
      id: 'south_hilly_20',
      name: 'South Campus Hilly Loop',
      terrain: 'hilly',
      durationMin: 20,
      distanceM: null,           // computed by ORS at runtime
      walkScorePct: 63.0,        // avg of south campus segments (063,064,067,065)
      greenScore: 88,            // south campus has high AE1+AE2 (AE1=5,AE2=5 on seg 065,067)
      avgGradient: 2.0,          // mix of AC4=1 steep and AC4=3-5 moderate segments
      emotions: ['Energized', 'Happy'],
      // Meditation spot: upper south campus junction — green, quiet, mid-descent
      meditationSpot: [40.60389, -75.38079],
      coordinates: null,         // ORS generates at runtime from orsWaypoints
      // ── ORS waypoints: [lng, lat] ──
      // Chain forces route south into lower campus (elevation drops ~30ft) then loops back
      orsWaypoints: [
        [-75.37828, 40.60724],   // START: upper campus junction
        [-75.37833, 40.60822],   // north arc (seg 027 area)
        [-75.38110, 40.60797],   // northwest high point (seg 095 area)
        [-75.38079, 40.60389],   // south lower campus (seg 063 start, lower elevation)
        [-75.37924, 40.60352],   // south valley junction (seg 064 end)
        [-75.37832, 40.60387],   // seg 065/067 area (south east)
        [-75.37637, 40.60681],   // reconnect to main level (seg 124/304 area)
        [-75.37828, 40.60724],   // END: loop close
      ],
    },
  
  ];
  
  // ─────────────────────────────────────────────────────────────────────────────
  // selectRoute(emotionLabel, durationMin, terrain)
  //
  // Returns the best matching route object.
  // If the route has `coordinates`, use them directly (curated CSV route).
  // If the route has `orsWaypoints`, call ORS with those waypoints (see App.js).
  // Returns null only if LEHIGH_ROUTES is completely empty.
  //
  // terrain values: 'flat' | 'mixed' | 'hilly'
  //   'mixed' tries hilly first then flat — gives the most variety
  // ─────────────────────────────────────────────────────────────────────────────
  export function selectRoute(emotionLabel, durationMin, terrain = 'flat') {
    if (LEHIGH_ROUTES.length === 0) return null;
  
    // For 'mixed', try hilly first then fall back to flat
    const terrainOrder =
      terrain === 'flat'  ? ['flat'] :
      terrain === 'hilly' ? ['hilly', 'flat'] :
      /* mixed */           ['hilly', 'flat'];
  
    for (const t of terrainOrder) {
      // Priority 1: terrain + duration + emotion all match
      const exactMatch = LEHIGH_ROUTES
        .filter(r =>
          r.terrain === t &&
          r.durationMin === durationMin &&
          r.emotions.includes(emotionLabel)
        )
        .sort((a, b) => b.greenScore - a.greenScore);
      if (exactMatch.length > 0) return exactMatch[0];
  
      // Priority 2: terrain + duration match (any emotion)
      const terrainDuration = LEHIGH_ROUTES
        .filter(r => r.terrain === t && r.durationMin === durationMin)
        .sort((a, b) => b.greenScore - a.greenScore);
      if (terrainDuration.length > 0) return terrainDuration[0];
    }
  
    // Priority 3: any route with matching duration
    const anyDuration = LEHIGH_ROUTES
      .filter(r => r.durationMin === durationMin)
      .sort((a, b) => b.greenScore - a.greenScore);
    if (anyDuration.length > 0) return anyDuration[0];
  
    // Priority 4: absolute fallback — closest duration
    return LEHIGH_ROUTES.sort(
      (a, b) => Math.abs(a.durationMin - durationMin) - Math.abs(b.durationMin - durationMin)
    )[0];
  }